/* Страница упражнения (EX-1, EX-2, EX-3) — замена табличной панели (TPL-2): сюда ведёт
   строка базы. Своё правится целиком и сохраняется само, по каждому полю. Общее из
   общей базы сервиса не меняется никак — его только смотрят. Новое — та же страница
   пустой: в базе оно появляется, как только у него есть название. */
import { mediaUrl, removeCatalogExercise, trpc, upsertCatalogExercise, useCatalog } from '@tg/app-core';
import { blockUsesEx, type Block, type Exercise, type VideoRef } from '@tg/domain';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMe, useTemplates } from '../lib/data';
import { BODY_RU, GROUP_ORDER, muscleRu, PMNAMES } from '../lib/dict';
import { exIni } from '../lib/format';
import { usePageTitle } from '../shell/Shell';
import { ICON } from '../ui/icons';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';
import { AddVideoModal, EXT_SVG, openLink, useExLink, useExVideo, useVideoUpload, VideoPlayer } from '../ui/Video';
import { Svg } from '../constructor/Svg';
import { ICON as CICON } from '../constructor/icons';

const EX_UNITS = ['кг', 'повт', 'сек', 'м', 'кал', 'темп', 'скорость', 'мощность', 'высота', 'пульс', 'RPE'];

/* Поля сохраняются по blur (as-is, без кнопки) — но Enter должен коммитить тоже,
   иначе после ввода названия нового упражнения кажется, что страница зависла. */
const blurOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') e.currentTarget.blur();
};

/* Техника и акценты в базе — список строк, но у части упражнений общей базы это одна строка */
const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : typeof v === 'string' && v.trim() ? [v] : []);

type Field = { ru: string; en: string; groups: string[]; equipment: string; units: string[]; pm: string | null; target: string; secondary: string; steps: string; tips: string };

function toField(e: Exercise | undefined): Field {
  return {
    ru: e?.ru || '',
    en: e?.en || '',
    groups: e?.groups || [],
    equipment: e && e.equipment !== '—' ? e.equipment : '',
    units: e?.units || ['кг', 'повт'],
    pm: e?.pm || null,
    target: e?.target || '',
    secondary: (e?.secondary || []).join(', '),
    steps: '',
    tips: '',
  };
}

const Thumb = ({ img, ru, en }: { img?: string | null; ru: string; en: string }) => (
  <span className="thumb eth">{img ? <img src={mediaUrl(img)} alt="" /> : ru ? exIni({ ru, en }) : <ICON.dumb />}</span>
);

export function ExercisePage({ isNew }: { isNew?: boolean }) {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const cat = useCatalog();
  /* «Расчёт от максимума»: первым — само упражнение (pm пустой — ключ 1ПМ его собственный, domain pmKey),
     затем основные движения (они есть в рекордах у каждого клиента), свои упражнения, остальная база — по ключу 1ПМ
     (как в рекордах клиента: вариации одного движения делят ключ), по алфавиту; поиск — в списке (app-core/dropdowns.ts) */
  const pmOpts = useMemo(() => {
    const seen = new Set(Object.keys(PMNAMES));
    const mine: [string, string][] = [],
      base: [string, string][] = [];
    for (const e of cat.list) {
      if (e.id === id || !e.units.includes('кг')) continue;
      const k = e.pm || e.id;
      if (seen.has(k)) continue;
      seen.add(k);
      (e.own ? mine : base).push([k, e.ru]);
    }
    const abc = (a: [string, string], b: [string, string]) => a[1].localeCompare(b[1], 'ru');
    return { main: Object.entries(PMNAMES), mine: mine.sort(abc), base: base.sort(abc) };
  }, [cat.list, id]);
  const utils = trpc.useUtils();
  const e = isNew ? undefined : cat.ex(id);
  const own = isNew || !!e?.own;
  const notFound = !isNew && cat.ready && !e;
  usePageTitle(isNew ? 'Новое упражнение' : 'Упражнение');

  const det = trpc.catalog.exerciseDetail.useQuery({ id }, { enabled: !isNew && !!e, staleTime: Infinity });
  const [f, setF] = useState<Field>(() => toField(e));
  const [newGroup, setNewGroup] = useState('');
  /* Группы упражнений (EX-4): фиксированный список + те, что тренер уже завёл сам в базе, + свои группы ЭТОГО
     упражнения прямо сейчас (даже пока не сохранилось, например без названия) — иначе только что вписанную
     группу негде отжать обратно, её не видно как чип (CLI отзыв: «создаю свою, но не могу потом её отвязать») */
  const exGroups = useMemo(
    () => [...GROUP_ORDER, ...[...new Set([...cat.list.flatMap((x) => x.groups), ...f.groups])].filter((g) => g && !GROUP_ORDER.includes(g))],
    [cat.list, f.groups],
  );
  const toggleGroup = (g: string) => commit({ groups: f.groups.includes(g) ? f.groups.filter((x) => x !== g) : [...f.groups, g] });
  const addGroup = () => {
    const g = newGroup.trim();
    if (!g || f.groups.includes(g)) return setNewGroup('');
    commit({ groups: [...f.groups, g] });
    setNewGroup('');
  };
  const stepsLoaded = useRef(false);
  useEffect(() => {
    if (stepsLoaded.current || !det.data) return;
    stepsLoaded.current = true;
    setF((p) => ({ ...p, steps: asList(det.data?.steps).join('\n'), tips: asList(det.data?.tips).join('\n') }));
  }, [det.data]);

  const EQUIP = useMemo(() => {
    const n: Record<string, number> = {};
    cat.list.forEach((x) => (n[x.equipment] = (n[x.equipment] || 0) + 1));
    return Object.keys(n)
      .filter((x) => x !== '—' && x)
      .sort((a, b) => n[b] - n[a]);
  }, [cat.list]);

  const save = trpc.trainer.saveExercise.useMutation({
    onSuccess: (x) => {
      upsertCatalogExercise(utils, x);
      utils.catalog.exerciseDetail.invalidate({ id: x.id });
    },
    onError: (er) => toast(er.message || 'Не сохранилось'),
  });
  const del = trpc.trainer.deleteExercise.useMutation({
    onSuccess: () => {
      if (e) removeCatalogExercise(utils, e.id);
      toast('Упражнение удалено из вашей базы');
      nav('/exercises');
    },
    onError: (er) => toast(er.message || 'Не удалось удалить'),
  });

  const commit = (patch: Partial<Field>) => {
    const next = { ...f, ...patch };
    setF(next);
    if (!own) return;
    if (!next.ru.trim()) {
      if (e) toast('Укажите название упражнения');
      return;
    }
    save.mutate(
      {
        id: e?.id,
        ru: next.ru.trim(),
        en: next.en.trim() || next.ru.trim(),
        groups: next.groups,
        equipment: next.equipment || '—',
        units: next.units,
        pm: next.pm,
        target: next.target.trim() || undefined,
        secondary: next.secondary
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        steps: next.steps
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        tips: next.tips
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      },
      {
        onSuccess: (x) => {
          if (!e) {
            toast('Упражнение добавлено в вашу базу');
            nav('/exercises/' + x.id, { replace: true });
          } else toast('Сохранено');
        },
      },
    );
  };
  const toggleUnit = (u: string) => {
    const has = f.units.includes(u);
    const next = has ? f.units.filter((x) => x !== u) : [...f.units, u];
    if (!next.length) return toast('Нужен хотя бы один показатель');
    commit({ units: next });
  };

  /* Похожие и замены — соседи по базе: та же целевая мышца и часть тела (только для общих) */
  const rel = useMemo(() => {
    if (!e || e.own || !e.bodyPart) return null;
    const words = new Set(e.en.split(/[^a-z0-9]+/));
    const near = (x: Exercise) => -x.en.split(/[^a-z0-9]+/).filter((w) => words.has(w)).length;
    const same = cat.list.filter((x) => !x.own && x.id !== e.id && x.target === e.target && x.bodyPart === e.bodyPart).sort((p, q) => near(p) - near(q));
    return { similar: same.filter((x) => x.equipment === e.equipment).slice(0, 8), subs: same.filter((x) => x.equipment !== e.equipment).slice(0, 6) };
  }, [e, cat.list]);

  const blocksQ = useTemplates('block');
  const uses = useMemo(() => {
    if (!e || !blocksQ.data) return [];
    return blocksQ.data.filter((t) => blockUsesEx(t.content as unknown as Block, e.id));
  }, [e, blocksQ.data]);

  const [confirmDel, setConfirmDel] = useState(false);
  const videoOn = !!useMe().data?.videoOn;
  /* Видео тренера к упражнению (VID-2) — и к своему, и к общему: видно только ему и его клиентам,
     в тренировки подставляется само */
  const saveVid = useExVideo();
  const setVid = (video: VideoRef | null) =>
    e &&
    saveVid(e.id, video).then(
      () => toast(video ? 'Видео сохранено — оно будет во всех ваших тренировках с этим упражнением' : 'Видео убрано из упражнения'),
      () => toast('Не удалось сохранить — попробуйте ещё раз'),
    );
  const vu = useVideoUpload(setVid, (msg) => toast(msg), f.ru);
  const video = e?.video ?? null;
  /* Ссылка на ролик на видеохостинге (VID-3) — рядом со своим видео, можно и то и другое */
  const link = e?.link ?? null;
  const saveLink = useExLink();
  const [addLink, setAddLink] = useState(false);
  const setLink = (url: string | null) =>
    e &&
    saveLink(e.id, url).then(
      () => toast(url ? 'Ссылка сохранена — она будет во всех ваших тренировках с этим упражнением' : 'Ссылка убрана из упражнения'),
      (err) => {
        if (url) throw err;
        toast('Не удалось убрать ссылку — попробуйте ещё раз');
      },
    );

  if (notFound) {
    return (
      <div className="cons one">
        <main>
          <section className="pane">
            <div className="empty">
              <div className="t">Упражнения нет в базе</div>
              <div className="d">
                <Link to="/exercises">Вернуться к упражнениям</Link>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }
  if (!isNew && !cat.ready) return null;

  const steps = own ? [] : asList(det.data?.steps);

  return (
    <div className="cons one">
      <main>
        <section className="pane">
          <div className="ehead">
            <nav className="crumb">
              <Link to="/exercises">Упражнения</Link>
              <span className="sep">/</span>
              <span className="cur">{f.ru || 'Новое упражнение'}</span>
            </nav>
            <div className="ehead-top">
              <Thumb img={e?.img} ru={f.ru} en={f.en} />
              <div className="eid">
                {own ? (
                  <input
                    className="enm"
                    value={f.ru}
                    placeholder="Название упражнения"
                    aria-label="Название упражнения"
                    onChange={(x) => setF({ ...f, ru: x.target.value })}
                    onBlur={() => commit({})}
                    onKeyDown={blurOnEnter}
                  />
                ) : (
                  <div className="enm">{f.ru}</div>
                )}
                <div className="esub">
                  {own ? <span className="chip ok">своё</span> : <span className="chip ghost">общая база</span>}
                  {f.en && <span>{f.en}</span>}
                  {f.groups.length > 0 && <span>{f.groups.join(', ')}</span>}
                  {f.equipment && <span>{f.equipment}</span>}
                </div>
              </div>
              <div className="eacts">
                {e && (
                  <Link className="btn" to="/constructor">
                    <ICON.build /> В тренировку
                  </Link>
                )}
                {e && own && (
                  <button className="btn gh" onClick={() => setConfirmDel(true)}>
                    Удалить
                  </button>
                )}
              </div>
            </div>
          </div>
          {!own && (
            <div className="ebar">
              <span>
                <b>Упражнение из общей базы</b>
                <s>Описание не меняется. Своё видео к нему добавить можно — его видите только вы и ваши клиенты.</s>
              </span>
            </div>
          )}
          {isNew && !e && (
            <div className="ebar">
              <span>
                <b>Новое упражнение</b>
                <s>Появится в вашей базе, как только вы дадите ему название. Остальное — по желанию, всё сохраняется само.</s>
              </span>
            </div>
          )}

          <div className="egrid">
            <div>
              <div className="sechead">
                <span className="t">Основное</span>
                <span className="ln"></span>
              </div>
              <div className="card cb ecard">
                {own ? (
                  <>
                    <div className="cols2" style={{ marginBottom: 12 }}>
                      <div className="formfld">
                        <span className="k">Название по-английски</span>
                        <input className="inp" value={f.en} placeholder="Например: Back Squat" onChange={(x) => setF({ ...f, en: x.target.value })} onBlur={() => commit({})} onKeyDown={blurOnEnter} />
                      </div>
                      <div className="formfld">
                        <span className="k">Оснащение</span>
                        <select className="inp" value={f.equipment} onChange={(x) => commit({ equipment: x.target.value })}>
                          <option value="">Без оснащения</option>
                          {EQUIP.map((x) => (
                            <option key={x}>{x}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="formfld" style={{ marginBottom: 12 }}>
                      <span className="k">Группы · можно отметить несколько</span>
                      <div className="tags">
                        {exGroups.map((g) => (
                          <button key={g} type="button" className={f.groups.includes(g) ? 'on' : ''} onClick={() => toggleGroup(g)}>
                            {g}
                          </button>
                        ))}
                      </div>
                      <div className="exnewgrp">
                        <input
                          className="inp"
                          value={newGroup}
                          placeholder="Своя группа — впишите название и нажмите Enter"
                          onChange={(x) => setNewGroup(x.target.value)}
                          onBlur={addGroup}
                          onKeyDown={(x) => x.key === 'Enter' && (x.currentTarget.blur(), addGroup())}
                        />
                      </div>
                    </div>
                    <div className="cols2" style={{ marginBottom: 12 }}>
                      <div className="formfld">
                        <span className="k">Целевые мышцы</span>
                        <input className="inp" value={f.target} placeholder="Например: квадрицепсы, ягодичные" onChange={(x) => setF({ ...f, target: x.target.value })} onBlur={() => commit({})} onKeyDown={blurOnEnter} />
                      </div>
                      <div className="formfld">
                        <span className="k">Вспомогательные мышцы</span>
                        <input className="inp" value={f.secondary} placeholder="Например: мышцы кора" onChange={(x) => setF({ ...f, secondary: x.target.value })} onBlur={() => commit({})} onKeyDown={blurOnEnter} />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="kv" style={{ marginTop: 0 }}>
                    {f.en && (
                      <>
                        <s>Название по-английски</s>
                        <b>{f.en}</b>
                      </>
                    )}
                    <s>Группы</s>
                    <b>{f.groups.join(', ') || '—'}</b>
                    <s>Оснащение</s>
                    <b>{f.equipment || 'Без оснащения'}</b>
                    {e?.bodyPart && (
                      <>
                        <s>Часть тела</s>
                        <b>{BODY_RU[e.bodyPart] || e.bodyPart}</b>
                      </>
                    )}
                    {e?.target && (
                      <>
                        <s>Целевая мышца</s>
                        <b>{muscleRu(e.target)}</b>
                      </>
                    )}
                    {!!e?.secondary?.length && (
                      <>
                        <s>Вспомогательные</s>
                        <b>
                          {e.secondary.map((x) => (
                            <span className="chip" key={x}>
                              {muscleRu(x)}
                            </span>
                          ))}
                        </b>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="sechead">
                <span className="t">Показатели</span>
                <span className="ln"></span>
              </div>
              <div className="card cb ecard">
                {own ? (
                  <div className="tags">
                    {EX_UNITS.map((u) => (
                      <button key={u} className={f.units.includes(u) ? 'on' : ''} onClick={() => toggleUnit(u)}>
                        {u}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div>
                    {f.units.map((u) => (
                      <span className="chip" key={u}>
                        {u}
                      </span>
                    ))}
                  </div>
                )}
                <div className="setrow pmrow" style={{ marginTop: 12 }}>
                  <span className="t">
                    <b>Расчёт от максимума</b>
                    <s>Процент в тренировке считается от 1ПМ этого движения</s>
                  </span>
                  {own ? (
                    <select className="inp" style={{ width: 220 }} value={f.pm || ''} onChange={(x) => commit({ pm: x.target.value || null })}>
                      <option value="">{f.units.includes('кг') ? 'Это упражнение' : 'Нет'}</option>
                      {(
                        [
                          ['Основные движения', pmOpts.main],
                          ['Мои упражнения', pmOpts.mine],
                          ['База упражнений', pmOpts.base],
                        ] as const
                      ).map(([g, list]) =>
                        list.length ? (
                          <optgroup key={g} label={g}>
                            {list.map(([k, n]) => (
                              <option key={k} value={k}>
                                {n}
                              </option>
                            ))}
                          </optgroup>
                        ) : null,
                      )}
                    </select>
                  ) : (
                    <b>
                      {f.pm ? (
                        PMNAMES[f.pm] || cat.ex(f.pm)?.ru || f.pm
                      ) : (
                        <span style={{ color: 'var(--tx3)', fontWeight: 500 }}>{f.units.includes('кг') ? 'это упражнение' : 'нет'}</span>
                      )}
                    </b>
                  )}
                </div>
              </div>

              <div className="sechead">
                <span className="t">Техника</span>
                <span className="ln"></span>
              </div>
              <div className="card cb ecard">
                {own ? (
                  <>
                    <textarea className="inp" rows={5} placeholder="Каждый шаг — с новой строки" value={f.steps} onChange={(x) => setF({ ...f, steps: x.target.value })} onBlur={() => commit({})} />
                    <div className="hint">Клиент видит шаги в тренировке, по кнопке техники у упражнения.</div>
                  </>
                ) : steps.length ? (
                  <ol className="rsteps">
                    {steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                ) : (
                  <div className="hint" style={{ marginTop: 0 }}>
                    В базе нет описания техники.
                  </div>
                )}
              </div>

              {own && (
                <>
                  <div className="sechead">
                    <span className="t">Акценты тренера</span>
                    <span className="ln"></span>
                  </div>
                  <div className="card cb ecard">
                    <textarea className="inp" rows={3} placeholder="На что смотреть в технике: ваши подсказки поверх шагов" value={f.tips} onChange={(x) => setF({ ...f, tips: x.target.value })} onBlur={() => commit({})} />
                    <div className="hint">Клиент видит их над шагами техники.</div>
                  </div>
                </>
              )}

              {rel && rel.similar.length > 0 && (
                <>
                  <div className="sechead">
                    <span className="t">Похожие</span>
                    <span className="ln"></span>
                    <span className="n">та же мышца и оснащение · {rel.similar.length}</span>
                  </div>
                  <div className="card cb ecard">
                    <div className="uses">
                      {rel.similar.map((x) => (
                        <Link key={x.id} to={'/exercises/' + x.id}>
                          <b>{x.ru}</b>
                          <s>{x.equipment}</s>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {rel && rel.subs.length > 0 && (
                <>
                  <div className="sechead">
                    <span className="t">Замены</span>
                    <span className="ln"></span>
                    <span className="n">та же мышца, другое оснащение · {rel.subs.length}</span>
                  </div>
                  <div className="card cb ecard">
                    <div className="uses">
                      {rel.subs.map((x) => (
                        <Link key={x.id} to={'/exercises/' + x.id}>
                          <b>{x.ru}</b>
                          <s>{x.equipment}</s>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {e && (
                <>
                  <div className="sechead">
                    <span className="t">Где используется</span>
                    <span className="ln"></span>
                    <span className="n">блоки базы</span>
                  </div>
                  <div className="card cb ecard">
                    {uses.length ? (
                      <div className="uses">
                        {uses.map((t) => (
                          <div key={t.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
                            <b style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: 14.5 }}>{t.title}</b>
                            <s style={{ fontSize: 13.5, color: 'var(--tx3)', whiteSpace: 'nowrap' }}>{t.folder || (t.trainerId ? '' : 'общая база')}</s>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="hint" style={{ marginTop: 0 }}>
                        Пока нет ни в одном блоке базы.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="side">
              {e && (videoOn || video) && (
                <div className="card cb ecard">
                  {vu.input}
                  <div className="sechead" style={{ margin: '0 0 10px' }}>
                    <span className="t">Ваше видео</span>
                    <span className="ln"></span>
                    {video && !vu.busy && (
                      <>
                        <button className="lnk" onClick={vu.pick}>
                          Заменить
                        </button>
                        <button className="lnk" style={{ color: 'var(--tx3)' }} onClick={() => setVid(null)}>
                          Убрать
                        </button>
                      </>
                    )}
                  </div>
                  {vu.busy ? (
                    <div className="vdrop up">
                      <b>Загружаем видео · {vu.pct}%</b>
                      <span className="vbar">
                        <i style={{ width: vu.pct + '%' }} />
                      </span>
                      <s>Не закрывайте страницу до конца загрузки</s>
                    </div>
                  ) : video ? (
                    <VideoPlayer video={video} />
                  ) : (
                    <button
                      className="vdrop"
                      onClick={vu.pick}
                    >
                      <Svg src={CICON.video} />
                      <b>Загрузить видео</b>
                      <s>Снимите технику на телефон — видео само подставится во все ваши тренировки с этим упражнением. Видите его только вы и ваши клиенты. MP4 или MOV, до 4 ГБ</s>
                    </button>
                  )}
                </div>
              )}
              {e && (
                <div className="card cb ecard">
                  <div className="sechead" style={{ margin: '0 0 10px' }}>
                    <span className="t">Ссылка на видео</span>
                    <span className="ln"></span>
                    {link && (
                      <>
                        <button className="lnk" onClick={() => setAddLink(true)}>
                          Заменить
                        </button>
                        <button className="lnk" style={{ color: 'var(--tx3)' }} onClick={() => setLink(null)}>
                          Убрать
                        </button>
                      </>
                    )}
                  </div>
                  {link ? (
                    <button className="vlink" onClick={() => openLink(link)} title={link.url}>
                      <Svg src={EXT_SVG} />
                      <b>{link.host}</b>
                      <s>{link.url}</s>
                    </button>
                  ) : (
                    <button className="vdrop lnkd" onClick={() => setAddLink(true)}>
                      <Svg src={EXT_SVG} />
                      <b>Добавить ссылку</b>
                      <s>Ролик на YouTube, Rutube, VK Видео, в Instagram или Telegram — клиент откроет его там. Подставится во все ваши тренировки с этим упражнением</s>
                    </button>
                  )}
                  {addLink && <AddVideoModal title={'Ссылка на видео · ' + f.ru} link onLink={(u) => setLink(u) as Promise<void>} onClose={() => setAddLink(false)} />}
                </div>
              )}
              {e?.gif && (
                <div className="card cb ecard">
                  <div className="sechead" style={{ margin: '0 0 10px' }}>
                    <span className="t">Демонстрация</span>
                  </div>
                  <img className="egif" src={mediaUrl(e.gif)} alt={f.ru + ' — демонстрация'} />
                </div>
              )}
              <div className="card cb ecard">
                <div className="kv" style={{ marginTop: 0 }}>
                  <s>Источник</s>
                  <b>{own ? 'ваша база' : 'общая база сервиса'}</b>
                  {e && (
                    <>
                      <s>В блоках</s>
                      <b>{uses.length}</b>
                    </>
                  )}
                  {!own && e && (
                    <>
                      <s>ID в базе</s>
                      <b>{e.id}</b>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      {confirmDel && e && (
        <Modal
          title="Удалить упражнение"
          onClose={() => setConfirmDel(false)}
          foot={
            <>
              <span className="sp"></span>
              <button className="btn gh" onClick={() => setConfirmDel(false)}>
                Отмена
              </button>
              <button className="btn rm" disabled={del.isPending} onClick={() => del.mutate({ id: e.id })}>
                Удалить
              </button>
            </>
          }
        >
          <p className="lead">«{e.ru}» уйдёт из вашей базы. В блоках и тренировках, где оно уже стоит, упражнение останется.</p>
        </Modal>
      )}
    </div>
  );
}
